import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import MarketingNav from "@/components/site/MarketingNav";
import Footer from "@/components/site/Footer";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, Copy, Sparkles, Eye, Type, ImageIcon, Film } from "lucide-react";

const KIND_ICON = { text: Type, image: ImageIcon, video: Film };

export default function Share() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/public/share/${slug}`)
      .then((r) => {
        setData(r.data);
        // Also capture ref cookie so if they browse away and sign up later, it still credits
        if (r.data?.ref_code) {
          localStorage.setItem("draftwave.ref_code", r.data.ref_code.toLowerCase());
        }
        // Bump view count fire-and-forget
        api.post(`/public/share/${slug}/view`).catch(() => {});
      })
      .catch((e) => { if (e?.response?.status === 404) setNotFound(true); })
      .finally(() => setLoading(false));
  }, [slug]);

  function copyOutput() {
    if (data?.output) {
      navigator.clipboard.writeText(data.output);
      toast.success("Copied");
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex flex-col" data-testid="share-loading">
        <MarketingNav />
        <div className="flex-1 flex items-center justify-center overline text-muted-foreground">Loading…</div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-background flex flex-col" data-testid="share-not-found">
        <MarketingNav />
        <div className="flex-1 flex flex-col items-center justify-center gap-4 p-8">
          <div className="overline text-muted-foreground">404</div>
          <h1 className="font-serif-display text-5xl leading-none">That draft has vanished.</h1>
          <p className="text-muted-foreground text-sm">The share link may have been revoked or never existed.</p>
          <Link to="/"><Button className="rounded-none h-11 px-5 font-mono uppercase tracking-widest text-xs">Back to Draftwave <ArrowRight className="w-4 h-4 ml-1" /></Button></Link>
        </div>
        <Footer />
      </div>
    );
  }

  const Icon = KIND_ICON[data.kind] || Type;
  const signupHref = `/login${data?.ref_code ? `?ref=${data.ref_code}` : ""}`;

  return (
    <div className="min-h-screen bg-background" data-testid="share-page">
      <MarketingNav />

      {/* ---- Hero card ---- */}
      <section className="border-b border-border">
        <div className="max-w-[1200px] mx-auto grid grid-cols-1 lg:grid-cols-12">
          <div className="lg:col-span-8 border-r border-border p-8 md:p-14">
            <div className="flex items-center gap-3 overline text-muted-foreground text-[10px]">
              <Icon className="w-3 h-3 text-primary" />
              <span>{data.kind?.toUpperCase()}{data.tool ? ` · ${data.tool}` : ""}{data.agent_label ? ` · via ${data.agent_label}` : ""}</span>
              <span className="ml-2 flex items-center gap-1"><Eye className="w-3 h-3" /> {data.view_count ?? 0} views</span>
            </div>
            <h1 className="font-serif-display text-4xl md:text-6xl leading-none tracking-tight mt-4">
              {data.title || `A draft from ${data.user_name || "a Draftwave maker"}.`}
            </h1>
            {data.prompt && (
              <div className="mt-6 border-l-2 border-primary pl-4">
                <div className="overline text-[10px] text-muted-foreground">The prompt</div>
                <div className="mt-1 font-mono text-sm">{data.prompt}</div>
              </div>
            )}
            <p className="mt-6 max-w-xl text-sm text-muted-foreground">
              Made in the Draftwave workspace. You get {" "}
              <span className="text-foreground font-mono">120 free credits</span> to make your own.
            </p>
          </div>
          <div className="lg:col-span-4 p-8 md:p-14 bg-foreground text-background flex flex-col justify-between gap-6">
            <div>
              <div className="overline opacity-60">Your turn</div>
              <div className="font-serif-display text-3xl md:text-4xl mt-3 leading-tight">
                Same idea.
                <br />
                <span className="italic text-primary">Yours to ship.</span>
              </div>
              <p className="mt-4 text-sm opacity-80">
                Sign up in 30 seconds. 120 credits on the house. No card required.
              </p>
            </div>
            <Link to={signupHref} data-testid="share-cta-signup">
              <Button className="rounded-none h-12 px-6 bg-primary text-primary-foreground hover:bg-primary/90 font-mono uppercase tracking-widest text-xs w-full">
                Get 120 free credits <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ---- Output ---- */}
      <section className="border-b border-border">
        <div className="max-w-[1200px] mx-auto p-6 md:p-12">
          <div className="flex items-center justify-between mb-6">
            <div className="overline text-muted-foreground">The draft</div>
            {data.kind === "text" && (
              <Button variant="outline" size="sm" onClick={copyOutput} className="rounded-none h-8 font-mono text-[10px] uppercase tracking-widest" data-testid="share-copy-btn">
                <Copy className="w-3 h-3" /> Copy
              </Button>
            )}
          </div>
          {data.kind === "text" && (
            <pre className="whitespace-pre-wrap font-mono text-sm leading-relaxed bg-muted/30 border border-border p-6 max-h-[70vh] overflow-auto" data-testid="share-output">
              {data.output}
            </pre>
          )}
          {data.kind === "image" && data.output && (
            <div className="flex items-center justify-center" data-testid="share-output">
              <img src={data.output} alt={data.title || "Shared image"} className="max-h-[70vh] border border-border" />
            </div>
          )}
          {data.kind === "video" && data.output && (
            <div className="flex items-center justify-center" data-testid="share-output">
              <video controls className="max-h-[70vh] border border-border">
                <source src={data.output} type="video/mp4" />
              </video>
            </div>
          )}
        </div>
      </section>

      {/* ---- Second CTA ---- */}
      <section className="border-b border-border bg-muted/30">
        <div className="max-w-[1200px] mx-auto p-10 md:p-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <div className="overline text-muted-foreground flex items-center gap-2"><Sparkles className="w-3 h-3 text-primary" /> Made with Draftwave</div>
            <h3 className="font-serif-display text-3xl md:text-4xl mt-3 leading-tight">One idea → a whole week of content.</h3>
            <p className="mt-2 max-w-xl text-sm text-muted-foreground">
              Blogs, social, email, SEO, images and Sora 2 video. All from one workspace. 120 free credits at signup.
            </p>
          </div>
          <Link to={signupHref} data-testid="share-cta-final">
            <Button className="rounded-none h-12 px-6 font-mono uppercase tracking-widest text-xs">
              Start free <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
